/**
 * Hash-based router for BeatDuel SPA.
 * Routes use #/path format. No server config needed.
 */

import { CREATOR_TRACK_SUBMISSION } from './features';

export interface Route {
  path: string;
  query: string;
  component: string;
  params: Record<string, string>;
  requiresAuth: boolean;
}

interface RouteDefinition {
  pattern: RegExp;
  component: string;
  paramNames: string[];
  requiresAuth: boolean;
}

const routes: RouteDefinition[] = [
  // Auth
  { pattern: /^\/login$/, component: 'Login', paramNames: [], requiresAuth: false },
  { pattern: /^\/register$/, component: 'Register', paramNames: [], requiresAuth: false },

  // Onboarding

  // Marketing landing (logged-out homepage). Logged-in visitors are bounced to
  // /duels by the App.svelte guard `$effect` — this route itself stays public.
  { pattern: /^\/$/, component: 'Landing', paramNames: [], requiresAuth: false },

  // Duels (view-only on web — browse, phase-aware detail, results/reveal;
  // claiming/voting are iOS-only. The legacy submit/leaderboard/score sub-pages
  // and user duel creation are gone — resolveHash() redirects their old URLs.)
  { pattern: /^\/duels$/, component: 'DuelBrowse', paramNames: [], requiresAuth: true },
  // Creator-portal duel page — anchored with a trailing `$` so the more-specific
  // `/duels/{id}/result` sub-route below still matches its own pattern; this one
  // only matches the bare `/duels/{id}` form.
  { pattern: /^\/duels\/(\d+)$/, component: 'DuelDetail', paramNames: ['id'], requiresAuth: true },
  { pattern: /^\/duels\/(\d+)\/result$/, component: 'DuelResult', paramNames: ['id'], requiresAuth: true },

  // Creator catalog (Curation Duels) — the old `/account/tracks` (My Submissions)
  // redirects to `/catalog` in resolveHash() below.
  { pattern: /^\/catalog$/, component: 'MyCatalog', paramNames: [], requiresAuth: true },
  { pattern: /^\/catalog\/add$/, component: 'CatalogAdd', paramNames: [], requiresAuth: true },

  // Account
  { pattern: /^\/account$/, component: 'AccountHub', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/profile$/, component: 'AccountProfile', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/security$/, component: 'AccountSecurity', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/notifications$/, component: 'AccountNotifications', paramNames: [], requiresAuth: true },
  {
    pattern: /^\/account\/notifications\/list$/,
    component: 'AccountNotificationsList',
    paramNames: [],
    requiresAuth: true,
  },
  { pattern: /^\/account\/stats$/, component: 'AccountStats', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/invite$/, component: 'AccountInvite', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/votes$/, component: 'AccountVotes', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/saved$/, component: 'AccountSaved', paramNames: [], requiresAuth: true },
  // Backwards-compat: old `/account/favorites` route still works, just resolves to the new component.
  { pattern: /^\/account\/favorites$/, component: 'AccountSaved', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/listened$/, component: 'AccountListened', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/suggestions$/, component: 'AccountSuggestions', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/activity$/, component: 'AccountActivity', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/genres$/, component: 'AccountGenres', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/plans$/, component: 'AccountPlans', paramNames: [], requiresAuth: true },
  { pattern: /^\/account\/blocked-users$/, component: 'AccountBlockedUsers', paramNames: [], requiresAuth: true },
  { pattern: /^\/unsubscribe$/, component: 'Unsubscribe', paramNames: [], requiresAuth: false },

  // Public
  { pattern: /^\/forgot$/, component: 'ForgotPassword', paramNames: [], requiresAuth: false },
  { pattern: /^\/reset-password$/, component: 'ResetPassword', paramNames: [], requiresAuth: false },
  { pattern: /^\/verify-email$/, component: 'VerifyEmail', paramNames: [], requiresAuth: false },
  { pattern: /^\/join-team\/([A-Za-z0-9]+)$/, component: 'JoinTeamLanding', paramNames: ['code'], requiresAuth: false },
  { pattern: /^\/search$/, component: 'Search', paramNames: [], requiresAuth: true },
  { pattern: /^\/leaderboard$/, component: 'Leaderboard', paramNames: [], requiresAuth: true },
  { pattern: /^\/season-wrap-up$/, component: 'SeasonWrapUp', paramNames: [], requiresAuth: true },
  { pattern: /^\/points$/, component: 'Points', paramNames: [], requiresAuth: true },
  { pattern: /^\/users\/([^/]+)$/, component: 'UserProfile', paramNames: ['username'], requiresAuth: true },
  { pattern: /^\/tracks\/(\d+)\/ai-score$/, component: 'TrackAiScore', paramNames: ['trackId'], requiresAuth: true },
  { pattern: /^\/tracks\/(\d+)\/report$/, component: 'ReportContent', paramNames: ['id'], requiresAuth: true },
  { pattern: /^\/submit-guide$/, component: 'SubmitGuide', paramNames: [], requiresAuth: false },
  { pattern: /^\/overview$/, component: 'Overview', paramNames: [], requiresAuth: true },
  { pattern: /^\/terms$/, component: 'TermsOfService', paramNames: [], requiresAuth: false },
  { pattern: /^\/privacy$/, component: 'PrivacyPolicy', paramNames: [], requiresAuth: false },
  { pattern: /^\/help$/, component: 'Help', paramNames: [], requiresAuth: false },
  // Catalog + duel-submission tracks share one detail page (Track Unification).
  // (Legacy `#/pool/{id}` bookmarks no longer resolve — the back-compat rewrite was
  // removed in the legacy cleanup.)
  { pattern: /^\/tracks\/(\d+)$/, component: 'TrackView', paramNames: ['id'], requiresAuth: true },
  {
    pattern: /^\/challenge\/([a-f0-9]{24})$/,
    component: 'ChallengeLanding',
    paramNames: ['token'],
    requiresAuth: false,
  },
  { pattern: /^\/series\/([a-f0-9]{24})$/, component: 'SeriesLanding', paramNames: ['token'], requiresAuth: false },

  // Artists (profile pages only — the artist leaderboard was removed)
  { pattern: /^\/artists\/([^/]+)$/, component: 'ArtistProfile', paramNames: ['slug'], requiresAuth: true },

  // Feed
  { pattern: /^\/feed$/, component: 'Feed', paramNames: [], requiresAuth: true },

  // Friends
  { pattern: /^\/friends$/, component: 'Friends', paramNames: [], requiresAuth: true },

  // Games moved to iOS-only (remove-web-games); the Get-the-app promo page was
  // removed 2026-07-29 — the marketing Landing at `/` carries the app pitch now.
  // Every legacy `/play*` URL is redirected in `resolveHash()` below.

  // Onboarding
  { pattern: /^\/onboarding$/, component: 'Onboarding', paramNames: [], requiresAuth: true },

  // DMCA submit + counter-notice are public-facing and stay on the main domain.
  { pattern: /^\/dmca$/, component: 'DmcaSubmit', paramNames: [], requiresAuth: false },
  {
    pattern: /^\/dmca\/counter-notice\/(\d+)$/,
    component: 'DmcaCounterNotice',
    paramNames: ['claimId'],
    requiresAuth: false,
  },
];

const defaultRoute: Route = {
  path: '/',
  query: '',
  component: 'Landing',
  params: {},
  requiresAuth: false,
};

const notFoundRoute: Route = {
  path: '/404',
  query: '',
  component: 'NotFound',
  params: {},
  requiresAuth: false,
};

/** @internal Exported for unit testing */
export function parseHash(hash: string): { path: string; query: string } {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const qIdx = raw.indexOf('?');
  if (qIdx === -1) return { path: raw || '/', query: '' };
  return { path: raw.slice(0, qIdx) || '/', query: raw.slice(qIdx + 1) };
}

/** @internal Exported for unit testing */
export function matchRoute(parsed: { path: string; query: string }): Route {
  for (const route of routes) {
    const match = parsed.path.match(route.pattern);
    if (match) {
      const params: Record<string, string> = {};
      route.paramNames.forEach((name, i) => {
        params[name] = match[i + 1];
      });
      return {
        path: parsed.path,
        query: parsed.query,
        component: route.component,
        params,
        requiresAuth: route.requiresAuth,
      };
    }
  }
  return { ...notFoundRoute, path: parsed.path, query: parsed.query };
}

/**
 * A path-based deep link (`https://beatduel.com/challenge/{token}` or `/series/{token}`) —
 * the shareable form that iOS universal links can match (Apple matches the path, NOT the
 * #fragment). When the SPA loads at such a path, rewrite it to the equivalent hash route so
 * the existing hash router takes over and the user lands on the challenge/series page. On iOS
 * the AASA intercepts the link before the page loads, opening the app instead.
 * Runs once on load (the path is only set on initial navigation; hashchange never mutates it).
 */
export function canonicalizePath(): void {
  const path = window.location.pathname;

  const deepLink = path.match(/^\/(challenge|series)\/([a-f0-9]{24})$/);
  if (deepLink) {
    window.history.replaceState(null, '', `/#/${deepLink[1]}/${deepLink[2]}`);
    return;
  }

  // Game-pointing email CTAs use the bare path form (`/duels`) so the OS can hand
  // off to the app (AASA "/duels"); on web, rewrite to the duel browser hash route.
  if (path === '/duels') {
    window.history.replaceState(null, '', '/#/duels');
    return;
  }

  // Password-reset / email-verify emails link to the path form (`/reset-password?token=…`)
  // so iOS universal links (AASA) can intercept them and open the app. On web, rewrite to
  // the existing hash route the SPA already serves, preserving the ?token= query.
  if (/^\/(reset-password|verify-email)$/.test(path)) {
    window.history.replaceState(null, '', `/#${path}${window.location.search}`);
    return;
  }

  // Team invite links (`/join-team/{code}`) are path-based so iOS can intercept them via AASA.
  // On web, rewrite to the hash route.
  const joinTeamMatch = path.match(/^\/join-team\/([A-Za-z0-9]+)$/);
  if (joinTeamMatch) {
    window.history.replaceState(null, '', `/#/join-team/${joinTeamMatch[1]}`);
    return;
  }

  // Duel links (`/duels/{id}` or `/duels/{id}/x`) may arrive path-based — e.g. from
  // shared/OG links pointing at the creator-portal duel page. On web, rewrite to the
  // hash route, preserving any query string (the `?track=` deep-link param).
  const duelMatch = path.match(/^\/duels\/(.+)$/);
  if (duelMatch) {
    window.history.replaceState(null, '', `/#/duels/${duelMatch[1]}${window.location.search}`);
  }
}

// Run once on initial module load so a path-based deep link (challenge/series) gets
// canonicalized to its hash route before the first matchRoute call.
canonicalizePath();

// Legacy leaderboard routes collapsed into the player-only board. The bare
// `#/artists` and `#/tracks` (the old Artists/Tracks leaderboard tabs) and
// `#/top-picks` (the removed Top Picks feed) now redirect to `#/leaderboard`.
// Exact-path match only — never catches `#/artists/{slug}` or `#/tracks/{id}`.
const LEGACY_LEADERBOARD_PATHS = new Set(['/artists', '/tracks', '/top-picks']);

/**
 * @internal Exported for unit testing.
 * Resolve a hash to a route, redirecting removed leaderboard paths and legacy `/play*`
 * URLs (games moved to iOS-only; the Get-the-app promo page is gone — the root
 * Landing carries the app pitch, and the auth guard bounces logged-in users to /duels).
 */
export function resolveHash(hash: string): Route {
  const parsed = parseHash(hash);
  if (LEGACY_LEADERBOARD_PATHS.has(parsed.path)) {
    window.history.replaceState(null, '', '#/leaderboard');
    return matchRoute({ path: '/leaderboard', query: '' });
  }
  // The old `/play/leaderboard` duplicate route → the canonical `/leaderboard`.
  if (parsed.path === '/play/leaderboard') {
    window.history.replaceState(null, '', '#/leaderboard');
    return matchRoute({ path: '/leaderboard', query: '' });
  }
  // Every other legacy `/play*` URL (the removed promo page, ai-vs-you,
  // beat-market, echo, etc.) collapses onto the root Landing.
  if (parsed.path === '/play' || parsed.path.startsWith('/play/')) {
    window.history.replaceState(null, '', '#/');
    return matchRoute({ path: '/', query: '' });
  }
  // My Submissions became the creator catalog (Curation Duels) — the old
  // `/account/tracks` URL now lands on `/catalog` (My Catalog).
  if (parsed.path === '/account/tracks') {
    window.history.replaceState(null, '', '#/catalog');
    return matchRoute({ path: '/catalog', query: '' });
  }
  // Public portfolio pages removed with Beat Market — redirect to the user's profile
  const portfolioMatch = parsed.path.match(/^\/users\/([^/]+)\/portfolio$/);
  if (portfolioMatch) {
    const profilePath = `/users/${portfolioMatch[1]}`;
    window.history.replaceState(null, '', `#${profilePath}`);
    return matchRoute({ path: profilePath, query: '' });
  }
  // The play/vote/listen surface was removed (web is now a creator portal; voting is
  // iOS-only), and the Curation Duels cutover retired the per-duel submit/
  // leaderboard/score sub-pages — redirect all of their old URLs to the
  // phase-aware DuelDetail page.
  const duelSubpageMatch = parsed.path.match(/^\/duels\/(\d+)\/(play|submit|leaderboard|score)$/);
  if (duelSubpageMatch) {
    const detailPath = `/duels/${duelSubpageMatch[1]}`;
    const detailHash = parsed.query ? `${detailPath}?${parsed.query}` : detailPath;
    window.history.replaceState(null, '', `#${detailHash}`);
    return matchRoute({ path: detailPath, query: parsed.query });
  }
  // Creator song submission is switched off (decision 114): the add page lands on
  // My Catalog and the creator guide on the root Landing. Routes stay registered.
  if (!CREATOR_TRACK_SUBMISSION) {
    const target = parsed.path === '/catalog/add' ? '/catalog' : parsed.path === '/submit-guide' ? '/' : null;
    if (target) {
      window.history.replaceState(null, '', `#${target}`);
      return matchRoute({ path: target, query: '' });
    }
  }
  // User duel creation is retired (duels are system-generated from templates) —
  // the old create URL lands on the browse.
  if (parsed.path === '/duels/create') {
    window.history.replaceState(null, '', '#/duels');
    return matchRoute({ path: '/duels', query: '' });
  }
  return matchRoute(parsed);
}

// Reactive state — imported by App.svelte
let _currentRoute = $state<Route>(resolveHash(window.location.hash));

export function getCurrentRoute(): Route {
  return _currentRoute;
}

/**
 * Navigate to a new hash route.
 *
 * `_currentRoute` is updated eagerly/synchronously (mirroring `navigateReplace`
 * below) so that any code running immediately after `navigate()` returns —
 * notably the App.svelte auth-guard `$effect`, which re-runs on token changes
 * — already sees the destination route, not the one being left. Without this,
 * a synchronous state change (e.g. `logout()` clearing the token) followed by
 * `navigate('/login')` would leave `_currentRoute` pointing at the just-left
 * gated page until the `hashchange` event fires (a macrotask), letting the
 * guard wrongly treat that stale gated route as "the page requiring capture".
 *
 * The subsequent `hashchange` listener re-resolves the same hash via
 * `resolveHash()` and reassigns `_currentRoute` again — a no-op in effect
 * (same route, no additional side effects) that also applies any legacy-path
 * redirect `resolveHash` handles but `matchRoute` alone does not.
 *
 * Navigating to the already-current path is still a true no-op (matching the
 * browser's native behavior when `location.hash` is reassigned its current
 * value, which fires no `hashchange`): skip both the hash write and the
 * `_currentRoute` reassignment so callers that happen to `navigate()` to the
 * page they're already on don't trigger an extra, pointless reactive cycle
 * (refocus, lazy-component recheck, etc.).
 */
export function navigate(path: string): void {
  const hash = '#' + path;
  if (window.location.hash === hash) return;
  window.location.hash = hash;
  _currentRoute = matchRoute(parseHash(hash));
}

/**
 * Replace the current history entry with an app hash route WITHOUT leaving a
 * back-button entry, and — unlike a bare `history.replaceState` call — keeps
 * the reactive route state in sync immediately (`history.replaceState` never
 * fires `hashchange`, so `_currentRoute` would otherwise go stale until the
 * next real navigation). Uses an absolute `/#...` URL so it also resets
 * `location.pathname` to `/`, which matters when called from a non-root
 * pathname (e.g. an OAuth callback page like `/auth/google-callback`).
 */
export function navigateReplace(path: string): void {
  window.history.replaceState(null, '', '/#' + path);
  _currentRoute = matchRoute(parseHash('#' + path));
}

// Listen for hash changes
window.addEventListener('hashchange', () => {
  _currentRoute = resolveHash(window.location.hash);
  // Track page view in analytics (fire-and-forget)
  import('./analytics.svelte')
    .then(({ trackPageView }) => {
      trackPageView(_currentRoute.path);
    })
    .catch((e) => console.warn('Analytics page view tracking failed:', e));
});

export { defaultRoute };
